'use client';

import { LanguageEnum, type TalentLabel } from '@/__generated__/graphql/types';

import { defaultModulesNames } from '../../constants';
import type { TTalentUser } from '../use-get-user-data';
import { useTalentGetLabelsTranslationQuery } from './graphql/get-module-names.v5.generated';

// hook props
type TOptions =
  | {
      skip: false;
      companyId: number;
      user?: TTalentUser | null;
    }
  | {
      skip: true;
    };

export const useGetModuleNames = (options: TOptions) => {
  /* default talent module names - to view all the list go to:
https://docs.google.com/spreadsheets/d/1PxvFUXQZHrCbUZu6_RoRMw_RWuOQX0IvL9WqMqUUVMo/edit?gid=1795599488#gid=1795599488
*/
  const defaultModuleNames: TalentLabel[] = [...defaultModulesNames];

  const moduleNamesResponse = useTalentGetLabelsTranslationQuery({
    variables: options.skip
      ? undefined
      : {
          input: {
            company_id: options.companyId,
            labels: defaultModuleNames.map(label => {
              return label.label;
            }),
            language:
              options.user?.language ||
              options.user?.company?.language ||
              LanguageEnum.Es,
          },
        },
    skip: options.skip || !options.user,
    context: { clientName: 'v5' },
    notifyOnNetworkStatusChange: true,
  });

  /* maps error and return default modules names */
  if (
    moduleNamesResponse.error ||
    moduleNamesResponse.data?.talent?.talentHCM?.get_labels_translation
      ?.__typename === 'ResponseInternalError' ||
    moduleNamesResponse.data?.talent?.talentHCM?.get_labels_translation
      ?.__typename === 'ResponseValidationError'
  ) {
    return {
      ...moduleNamesResponse,
      moduleNames: defaultModuleNames,
    };
  }

  /* success response */
  if (
    moduleNamesResponse.data?.talent?.talentHCM?.get_labels_translation
      ?.__typename === 'TalentLabelList'
  ) {
    const moduleNames: TalentLabel[] = [
      ...moduleNamesResponse.data.talent.talentHCM.get_labels_translation
        .labels,
    ];

    return {
      ...moduleNamesResponse,
      moduleNames,
    };
  }

  /* default response */
  return {
    ...moduleNamesResponse,
    moduleNames: defaultModuleNames,
  };
};
